<!--
  StripeConnectCard -- Distributor App > Account > Payments.

  PR-4 of the distributor-payments redesign. Consumes PR-3's
  GET /api/distributor/billing/stripe-connect contract:

    { account_id_display, account_type, charges_enabled, payouts_enabled,
      transfers_capability, last_payout:{amount_cents,arrival_date}|null,
      dashboard_url, status }  where status in connected|not_connected|error

  Renders the masked account id, a status badge (connected=green,
  error/not_connected=amber/grey), a metadata line, and an
  "Open in Stripe" CTA that opens the Stripe dashboard in a new tab.
-->
<template>
  <v-card class="tf-connect-card" data-testid="stripe-connect-card">
    <v-card-title>Stripe Connect</v-card-title>
    <!--
      ONE relationship, not three (register :415-419; threadflow-stripe
      skill, "Three money flows, not one"). This connected account takes
      SHOPPER card payments as a direct charge with a ThreadFlow
      application fee. Decorator settlement is a Transfer that
      tf-deconetwork makes against a DIFFERENT connected account, and
      ThreadFlow bills the distributor by ACH as a platform Customer with
      no stripe_account header at all -- not Connect. The old copy named
      all three, which taught a wrong model on the one surface where a
      distributor forms it.
    -->
    <v-card-subtitle>
      Receives payments from shoppers who buy from your stores.
    </v-card-subtitle>

    <v-card-text>
      <div class="tf-connect-row">
        <div class="tf-connect-logo" aria-hidden="true">S</div>

        <div class="tf-connect-body">
          <div class="tf-connect-identity">
            <span v-if="isConnected" class="tf-connect-account">
              {{ connect.account_id_display }}
            </span>
            <span v-else class="tf-connect-account tf-connect-account--muted">
              No Stripe account connected
            </span>

            <v-chip
              :color="statusColor"
              size="small"
              label
              data-testid="stripe-connect-status"
              class="tf-connect-badge"
            >
              {{ statusLabel }}
            </v-chip>
          </div>

          <div class="tf-connect-meta">
            {{ metaLine }}
          </div>
        </div>

        <!--
          THE CTA ARMS. Two questions, deliberately NOT the same one.

          "Open in Stripe" asks whether Stripe answered: it needs a
          dashboard_url, which only the connected payload carries.

          "Disconnect" asks whether an account is ATTACHED, which is a fact
          about our own row and not about Stripe's mood. This used to share
          isConnected with the button above, and the comment here argued they
          were the same condition -- correct for not_connected (nothing is
          attached, so a deauthorize would name an account that does not
          exist) and WRONG for error, which means an account IS attached and
          the platform key simply cannot read it.

          That conflation was a dead end in the product, hit on production
          2026-09-24: an account authorized through the SANDBOX Connect app
          while the distributor was in live mode returned status="error", so
          no Disconnect rendered. Disconnecting is the FIRST STEP of the
          documented remedy -- the superadmin mode-flip endpoint 409s while
          an account is attached and its refusal names the order explicitly
          ("disconnect the Stripe account, switch the mode, re-run Connect
          OAuth") -- and it was the one step the UI did not offer.

          THE ERROR ARM SHOWS BOTH GROUPS, and that is deliberate. The
          connect CTAs are NOT dead at status="error": the OAuth callback's
          ownership check excludes the distributor's own row
          (``_distributor_owning(returned_id, excluding=distributor.id)``),
          so re-authorizing over your own attached account persists. Hiding
          them here to make room for Disconnect would have removed a path
          that works. Disconnect is ADDED to the error arm, never swapped in.
        -->
        <div v-if="isConnected" class="tf-connect-ctas">
          <v-btn
            :href="connect.dashboard_url"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            data-testid="stripe-connect-open"
            class="tf-connect-cta"
          >
            <v-icon start>open_in_new</v-icon>
            Open in Stripe
          </v-btn>

          <v-btn
            variant="text"
            color="error"
            size="small"
            data-testid="stripe-connect-disconnect"
            class="tf-connect-cta"
            @click="disconnectDialog = true"
          >
            <v-icon start>link_off</v-icon>
            Disconnect
          </v-btn>
        </div>

        <!--
          TWO PATHS, SIDE BY SIDE (plan section 6a, engineering choice E6).
          They stay visually and conceptually distinct because collapsing
          them is the original MVP gap: an existing-account affordance that
          silently fell back to Account.create mints a second EMPTY account
          behind a green "Connected" badge, and the distributor's real
          account is never attached.

          Plus, when an account is attached but unreadable, the way OUT --
          gated on isDisconnectable rather than on this arm, because the
          not_connected case reaches here too and there is genuinely nothing
          to disconnect then.
        -->
        <div v-else class="tf-connect-ctas">
          <v-btn
            :loading="connecting"
            variant="flat"
            color="primary"
            size="small"
            data-testid="stripe-connect-start"
            class="tf-connect-cta"
            @click="onConnect"
          >
            <v-icon start>arrow_forward</v-icon>
            Create new Stripe account
          </v-btn>

          <v-btn
            variant="outlined"
            size="small"
            data-testid="stripe-connect-existing-start"
            class="tf-connect-cta"
            @click="existingDialog = true"
          >
            <v-icon start>link</v-icon>
            Connect existing Stripe account
          </v-btn>

          <v-btn
            v-if="isDisconnectable"
            variant="text"
            color="error"
            size="small"
            data-testid="stripe-connect-disconnect"
            class="tf-connect-cta"
            @click="disconnectDialog = true"
          >
            <v-icon start>link_off</v-icon>
            Disconnect
          </v-btn>
        </div>
      </div>

      <!--
        THE READINESS LIST.

        The card used to carry ONE badge driven by `status`, which only ever
        meant "Stripe let us retrieve the account". On the real ACME sandbox
        account that badge was green while card payments were inactive and no
        shopper could be charged -- connection and readiness are different
        questions and the old card could only ask one of them.

        So each money flow gets its own row. The rules, from the Stripe
        recommendations register:

          * "Connected" (the badge above) describes AUTHORIZATION only.
          * "Ready" is reserved for an ACTIVE applicable capability.
          * "Action required" links to Stripe-hosted remediation.
          * "Under review" does NOT invite re-entering the same information.
          * A connection error never falls back to a cached green without
            also showing its age.

        Only rows applicable to this account's role are rendered -- a
        decorator sees settlement transfers, not shopper payments -- which is
        why each row is gated on its own status being present rather than on
        one profile check.
      -->
      <div
        v-if="readiness"
        class="tf-readiness"
        data-testid="stripe-readiness-list"
      >
        <div
          v-for="row in readinessRows"
          :key="row.key"
          class="tf-readiness-row"
          :data-testid="`stripe-readiness-${row.key}`"
        >
          <span class="tf-readiness-label">{{ row.label }}</span>
          <span
            class="tf-readiness-value"
            :class="`tf-readiness-value--${row.tone}`"
          >{{ row.label2 }}</span>
        </div>

        <div
          v-if="lastCheckedLabel"
          class="tf-readiness-row tf-readiness-row--meta"
          data-testid="stripe-readiness-last-checked"
        >
          <span class="tf-readiness-label">Last checked</span>
          <span class="tf-readiness-value">{{ lastCheckedLabel }}</span>
        </div>

        <!--
          The cached-data warning. Shown whenever we could not confirm the
          status just now, so the age is never implied to be "current".
        -->
        <v-alert
          v-if="isStaleOrErrored"
          type="warning"
          variant="tonal"
          density="compact"
          class="tf-connect-notice"
          data-testid="stripe-readiness-stale-warning"
        >
          We could not check with Stripe just now. The statuses above are the
          last confirmed values{{ staleAgeSuffix }}, so they may be out of
          date.
        </v-alert>

        <!--
          Remediation. Offered only when something is actually actionable:
          "under review" deliberately does NOT get this button, because
          sending someone back into onboarding to re-enter a value Stripe is
          already reviewing is how we would generate our own support tickets.
        -->
        <div v-if="needsRemediation" class="tf-readiness-actions">
          <v-btn
            :href="connect.dashboard_url"
            target="_blank"
            rel="noopener noreferrer"
            variant="flat"
            color="primary"
            size="small"
            data-testid="stripe-readiness-remediate"
          >
            <v-icon start>open_in_new</v-icon>
            Continue in Stripe
          </v-btn>
        </div>
      </div>

      <!--
        The distributor pressed Cancel on Stripe's authorization screen.
        An ordinary outcome, deliberately not styled as an error.
      -->
      <v-alert
        v-if="isDeclined"
        type="info"
        variant="tonal"
        density="compact"
        class="tf-connect-notice"
        data-testid="stripe-connect-declined"
      >
        {{ localOauthResult.message }}
      </v-alert>

      <!--
        THE NEVER-SILENT BRANCH -- a STOP-AND-REVIEW, not a confirmation.

        Stripe does not refuse an account controlled by another platform --
        it creates a SIBLING account and auto-connects it, returning a
        perfectly valid stripe_user_id for an account the distributor never
        meant to connect. Nothing is written until they acknowledge it here.

        WHY THE COPY IS OWNED HERE AND NOT TAKEN FROM `message`.
        The callback's `message` is a response-contract field. This panel
        needs prose that can be asserted on as SHIPPED text, and needs to
        say different things in different places (heading, consequence,
        the KYB tell). Rendering the server string as the explanation would
        put the copy requirement somewhere the frontend's tests cannot
        reach it -- so `message` is deliberately not shown on this branch.
        The declined branch above still renders it; that one IS just a
        sentence.

        THE COPY SAYS SEPARATE, never "linked to" or "sub-account": the two
        accounts share a Stripe LOGIN and share no charges, balances,
        customers, reports, or history. Describing them as linked is the
        misstatement that gets discovered at reconciliation, months later,
        after money has been routing into an account nobody reads.

        The KYB line is the load-bearing sentence. It is the one thing the
        distributor SAW with their own eyes minutes ago, and it is what
        turns "we think something odd happened" into a fact they can check.
      -->
      <v-alert
        v-if="needsAcknowledgement"
        type="warning"
        variant="tonal"
        class="tf-connect-notice tf-connect-stop-review"
        data-testid="stripe-connect-different-account"
      >
        <div
          class="tf-connect-stop-heading"
          data-testid="stripe-connect-different-heading"
        >
          Stop and check this account before continuing
        </div>

        <!--
          TWO BRANCHES, because only one of them is a MISMATCH.

          With a stated intent we can compare, and "this is not the account
          you asked for" is a fact. With none, nothing was compared -- the
          honest ground is that we cannot confirm it, and claiming a
          mismatch we cannot support is how a distributor learns to
          disbelieve this panel on the branch where it IS one.

          Both branches stop them, and both carry the same consequence and
          the same escape route below.
        -->
        <div v-if="localOauthResult.intended_account_id" class="tf-connect-swap-copy">
          This is not the Stripe account you asked for. Stripe created a
          brand new, separate account for ThreadFlow rather than connecting
          your existing one, which is what normally happens when your
          existing account is already managed by another platform.
        </div>

        <div v-else class="tf-connect-swap-copy">
          Check this is the Stripe account you meant to connect. If it is
          not one you recognize, Stripe may have created a brand new,
          separate account for ThreadFlow rather than connecting your
          existing one -- which is what normally happens when your existing
          account is already managed by another platform.
        </div>

        <div class="tf-connect-swap-copy">
          If Stripe asked you to verify your business details again during
          this connection, that is the signal: you would not be asked to
          re-verify a business on an account you already trade on.
        </div>

        <!--
          The consequence, split on the same fact as the paragraphs above:
          with an intent stated we KNOW this is a second account, without
          one we only know what would follow if it were. The ledger facts
          are identical either way -- separate balance, payouts, customers,
          reports, history -- which is the whole point of saying them.
        -->
        <div v-if="localOauthResult.intended_account_id" class="tf-connect-swap-copy">
          The new account uses the same Stripe login, but it is a separate
          account. It has its own balance, its own payouts, its own
          customers, and its own reports and transaction history. None of
          that is shared with the account you already use, so money taken
          through it will not appear in the reports you reconcile today.
        </div>

        <div v-else class="tf-connect-swap-copy">
          A second account would use the same Stripe login, but it is a
          separate account. It has its own balance, its own payouts, its own
          customers, and its own reports and transaction history. None of
          that is shared with the account you already use, so money taken
          through it will not appear in the reports you reconcile today.
        </div>

        <div v-if="localOauthResult.intended_account_id" class="tf-connect-swap-ids">
          <div>
            You asked to connect:
            <code>{{ localOauthResult.intended_account_id }}</code>
          </div>
          <div>
            Stripe connected:
            <code>{{ localOauthResult.returned_account_id }}</code>
          </div>
        </div>
        <div v-else class="tf-connect-swap-ids">
          <div>
            Stripe connected:
            <code>{{ localOauthResult.returned_account_id }}</code>
          </div>
        </div>

        <!--
          Nothing has been saved yet, said plainly. Without this the panel
          reads as "here is what we did", and the natural response to that
          is to accept it.
        -->
        <div class="tf-connect-swap-copy">
          Nothing has been saved. You can go back to Stripe and choose the
          account you meant.
        </div>

        <!--
          ACTION ORDER IS THE DESIGN.

          "Choose again" is first and is the filled button, because
          returning to Stripe is the right answer in almost every case that
          reaches this panel. Accepting the new account is second, styled
          as text, and NAMES what it accepts -- a sibling account is
          sometimes genuinely correct, so the escape hatch stays, but it
          must be a decision rather than a dismissal.
        -->
        <div class="tf-connect-swap-actions">
          <v-btn
            :loading="choosingAgain"
            variant="flat"
            color="primary"
            size="small"
            data-testid="stripe-connect-different-choose-again"
            @click="onChooseAgain"
          >
            <v-icon start>arrow_back</v-icon>
            Choose again
          </v-btn>
          <v-btn
            :loading="accepting"
            variant="text"
            size="small"
            data-testid="stripe-connect-different-confirm"
            @click="onAcceptDifferentAccount"
          >
            Use the new account anyway
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            data-testid="stripe-connect-different-cancel"
            @click="onCancelDifferentAccount"
          >
            Decide later
          </v-btn>
        </div>

        <!--
          A failed retry must not dismiss the panel: the sibling is
          unpersisted, the retry never happened, and this panel is the only
          thing explaining both.
        -->
        <div
          v-if="chooseAgainError"
          class="tf-connect-swap-error"
          data-testid="stripe-connect-different-error"
        >
          {{ chooseAgainError }}
        </div>
      </v-alert>
    </v-card-text>

    <!--
      BLANK-ID IS THE DEFAULT PATH, AND THE DIALOG HAS TO LOOK LIKE IT.

      The distributor continues to Stripe with nothing entered, signs in
      there, and picks the account in Stripe's own hosted UI; Stripe returns
      the authoritative stripe_user_id. That has always been how the
      protocol works -- `intended_account_id` is optional server-side and an
      empty value is treated as None -- but this dialog used to LEAD with
      the acct_ input, which asked most distributors for something they
      cannot find before letting them do the thing that works. Nothing about
      the handshake changed here; only which path looks supported.

      The acct_ id stays, behind Advanced, because it is the only signal the
      callback has for telling "the account you meant" from "the account
      Stripe made you": Stripe does not refuse an account controlled by
      another platform, it creates a SIBLING and auto-connects it, returning
      a perfectly valid id. Without a stated intent that substitution is
      indistinguishable from success. So it is de-emphasized, never deleted.

      Two things the copy must never imply, both misstatements the Stripe
      register names:

        - that the id is a CREDENTIAL. ThreadFlow never collects Stripe
          login information. Stripe performs the login, 2FA, account
          selection, authorization, and any onboarding.
        - that the id SELECTS the account at Stripe. It cannot. Entering it
          buys exactly one thing: we notice and tell you if Stripe hands
          back something else.
    -->
    <v-dialog
      v-model="existingDialog"
      max-width="520"
      data-testid="stripe-connect-existing-dialog"
    >
      <v-card>
        <v-card-title>Connect your existing Stripe account</v-card-title>
        <v-card-text>
          <p class="tf-connect-dialog-copy">
            We will send you to Stripe, where you sign in and choose the
            account you want to connect. Stripe handles the sign-in itself --
            ThreadFlow never sees your Stripe password.
          </p>
          <p class="tf-connect-dialog-copy">
            When you come back we will show you the account Stripe connected
            so you can confirm it is the right one.
          </p>

          <!--
            The safeguard, out of the way of the default path. Collapsed it
            renders NOTHING but this control, so the field cannot be the
            first thing a distributor reads and cannot be mistaken for a
            required step.
          -->
          <div class="tf-connect-advanced">
            <v-btn
              variant="text"
              size="small"
              class="tf-connect-advanced-toggle"
              data-testid="stripe-connect-existing-advanced-toggle"
              @click="advancedOpen = !advancedOpen"
            >
              <v-icon start>{{
                advancedOpen ? "expand_less" : "expand_more"
              }}</v-icon>
              Advanced (optional)
            </v-btn>

            <div v-if="advancedOpen" class="tf-connect-advanced-body">
              <p class="tf-connect-dialog-copy tf-connect-dialog-copy--hint">
                If you already know which Stripe account you are connecting,
                enter its ID and we will check it against the account Stripe
                returns, then warn you if they differ. It does not change
                which account Stripe connects -- you still choose that at
                Stripe. You will find it in your Stripe dashboard under
                Settings; it starts with <code>acct_</code>.
              </p>

              <v-text-field
                v-model="intendedAccountId"
                label="Expected Stripe account ID (optional)"
                placeholder="acct_..."
                density="compact"
                variant="outlined"
                data-testid="stripe-connect-existing-account-id"
              />
            </div>
          </div>

          <v-alert
            v-if="existingError"
            type="error"
            variant="tonal"
            density="compact"
            data-testid="stripe-connect-existing-error"
          >
            {{ existingError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            size="small"
            data-testid="stripe-connect-existing-cancel"
            @click="existingDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            :loading="startingOauth"
            variant="flat"
            color="primary"
            size="small"
            data-testid="stripe-connect-existing-submit"
            @click="onConnectExisting"
          >
            Continue to Stripe
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
      DISCONNECT CONFIRMATION.

      Disconnecting is not a toggle: it stops shopper charges through this
      account, and getting back means running the whole OAuth handshake
      again (or onboarding a new account). Both consequences are stated
      before the click that causes them, because neither is guessable from a
      button labeled "Disconnect" and neither is undoable from this page.

      THE FIRST PARAGRAPH SPLITS ON WHETHER STRIPE COULD READ THE ACCOUNT.
      The healthy sentence asserts that shoppers are being charged through
      this account today. When Stripe refused to return the account we have
      no evidence for that -- it may be revoked, deleted, or authorized
      through a different Connect application, in which case charges are
      already impossible and the sentence describes a loss that has already
      happened. Stating it anyway would be a confident claim about the one
      thing we just failed to check. The second paragraph is true in both
      states and is unchanged.

      Cancel is inert by construction -- it closes the dialog and nothing
      else. There is no optimistic local state to unwind, because the card
      never blanks itself: it waits for the parent's re-fetch.
    -->
    <v-dialog
      v-model="disconnectDialog"
      max-width="520"
      data-testid="stripe-connect-disconnect-dialog"
    >
      <v-card>
        <v-card-title>Disconnect this Stripe account?</v-card-title>
        <v-card-text>
          <p v-if="isUnreadableAccount" class="tf-connect-dialog-copy">
            <code>{{ connect.account_id_display }}</code> is attached to your
            ThreadFlow account, but it could not be read from Stripe just now,
            so we cannot say whether it is currently able to take payment.
            Disconnecting removes it here, and any store relying on it will
            stop taking payment.
          </p>
          <p v-else class="tf-connect-dialog-copy">
            Shoppers will no longer be able to be charged through
            <code>{{ connect.account_id_display }}</code>, and any store
            relying on it will stop taking payment.
          </p>
          <p class="tf-connect-dialog-copy">
            To reconnect later you will need to run the whole Stripe
            authorization flow again. Your Stripe account itself is not
            closed or changed -- only its connection to ThreadFlow is
            removed.
          </p>

          <v-alert
            v-if="disconnectError"
            type="error"
            variant="tonal"
            density="compact"
            data-testid="stripe-connect-disconnect-error"
          >
            {{ disconnectError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            size="small"
            data-testid="stripe-connect-disconnect-cancel"
            @click="onCancelDisconnect"
          >
            Cancel
          </v-btn>
          <v-btn
            :loading="disconnecting"
            variant="flat"
            color="error"
            size="small"
            data-testid="stripe-connect-disconnect-confirm"
            @click="onConfirmDisconnect"
          >
            Disconnect
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  acceptDifferentStripeAccount,
  disconnectStripeConnect,
  startStripeConnect,
  startStripeConnectOAuth,
} from "@/services/billingService";

export default {
  name: "StripeConnectCard",

  emits: ["connected"],

  props: {
    connect: {
      type: Object,
      required: true,
    },

    // The OAuth callback outcome, owned by PaymentsPage (which handles the
    // return leg) and mirrored into local state below. Mirrored rather than
    // read directly because the card dismisses it locally on Cancel and
    // after a successful accept, and a prop cannot be cleared by its child.
    oauthResult: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      // Guards the CTA while the onboarding-init round-trip is in flight, so a
      // double-click can't fire two Account.create calls.
      connecting: false,

      // Existing-account (OAuth) path.
      existingDialog: false,
      intendedAccountId: "",
      startingOauth: false,
      existingError: "",

      // The optional-id disclosure. Collapsed by default, which is the
      // whole point: blank-ID is the supported path, so the dialog must
      // open on the action rather than on a field most distributors cannot
      // fill in. Reset on close (see the existingDialog watcher) so a
      // reopened dialog is back at the default rather than carrying the
      // last visit's expanded state.
      advancedOpen: false,

      // Local mirror of the ``oauthResult`` prop. One of connected /
      // different_account / unverified_account / declined, or null before
      // any handshake.
      localOauthResult: this.oauthResult,
      accepting: false,

      // The "Choose again" path out of the stop-and-review panel. Separate
      // from `startingOauth` (which guards the dialog's submit button)
      // because both can be on screen in principle and a shared flag would
      // spin the wrong control.
      choosingAgain: false,
      chooseAgainError: "",

      // Disconnect (deauthorize) path.
      disconnectDialog: false,
      disconnecting: false,
      disconnectError: "",
    };
  },

  computed: {
    isConnected() {
      return this.connect && this.connect.status === "connected";
    },

    // Is there an account to disconnect? Deliberately NOT isConnected.
    //
    // The discriminating fact is the presence of an account_id, which the
    // endpoint sets from our OWN row before it ever calls Stripe: the
    // not_connected branch returns account_id: null, and BOTH the connected
    // and error branches return the masked id. So "error" -- an attached
    // account the platform key cannot read -- is disconnectable, while
    // "not_connected" stays excluded and no deauthorize is ever offered for
    // an account that does not exist.
    //
    // status === "connected" is kept as a first clause rather than relying on
    // account_id alone, because it is the field this card has always trusted
    // and an older API build that omits account_id must not silently lose the
    // disconnect control on a healthy account. The account_id clause is what
    // ADDS the error case.
    isDisconnectable() {
      if (!this.connect) return false;
      if (this.connect.status === "connected") return true;
      return !!this.connect.account_id;
    },

    // Stripe could not read the attached account, so anything this card says
    // about what the connection currently DOES is unevidenced. Used by the
    // confirmation dialog to state the consequence accurately.
    isUnreadableAccount() {
      return this.isDisconnectable && !this.isConnected;
    },

    // connected -> green; error/not_connected -> amber (warning) / grey.
    statusColor() {
      switch (this.connect?.status) {
        case "connected":
          return "success";
        case "error":
          return "warning";
        default:
          return "grey";
      }
    },

    statusLabel() {
      switch (this.connect?.status) {
        case "connected":
          return "Connected";
        case "error":
          return "Error";
        default:
          return "Not connected";
      }
    },

    // Stripe returned an account we could not confirm is the intended one --
    // either it differs from what they named, or they named nothing. Both
    // shapes get the SAME explicit confirmation. Treating "no intent" as
    // "anything is fine" would leave the hole the intent check closes,
    // reachable by leaving one field blank.
    needsAcknowledgement() {
      const status = this.localOauthResult && this.localOauthResult.status;
      return status === "different_account" || status === "unverified_account";
    },

    isDeclined() {
      return !!this.localOauthResult && this.localOauthResult.status === "declined";
    },

    // The normalized readiness contract from
    // services/connect_account_status. Null when nothing is connected --
    // there is no readiness to report about an account that does not exist.
    readiness() {
      return (this.connect && this.connect.readiness) || null;
    },

    // True when we could not CONFIRM the statuses just now. Covers both a
    // hard connection error and a snapshot that has aged past its freshness
    // window. Both must suppress green for the same reason: an unconfirmed
    // "ready" is a statement about the past.
    isStaleOrErrored() {
      const r = this.readiness;
      if (!r) return false;
      const conn = r.connection && r.connection.status;
      return (
        conn === "connection_error" ||
        conn === "unknown" ||
        conn === "environment_mismatch" ||
        r.cached === true ||
        r.is_stale === true
      );
    },

    // Rows are built from whatever the contract carries, so a decorator
    // (transfers, no shopper payments) and a distributor render from the
    // same code without a role branch in the template.
    readinessRows() {
      const r = this.readiness;
      if (!r) return [];

      const candidates = [
        { key: "shopper-payments", label: "Shopper payments", section: r.shopper_payments },
        { key: "transfers", label: "Settlement transfers", section: r.transfers },
        { key: "bank-payouts", label: "Bank payouts", section: r.bank_payouts },
        { key: "verification", label: "Stripe verification", section: r.verification },
      ];

      return candidates
        .filter((c) => c.section && c.section.status && c.section.status !== "not_used")
        .map((c) => ({
          key: c.key,
          label: c.label,
          label2: this.statusText(c.section.status),
          tone: this.statusTone(c.section.status),
        }));
    },

    // Only an ACTIONABLE state gets the remediation CTA. "under_review"
    // is excluded on purpose -- Stripe is checking something already
    // submitted, and inviting a re-submit there wastes the distributor's
    // time and generates a support ticket for us.
    needsRemediation() {
      return this.readinessRows.some(
        (row) => row.label2 === "Action required" || row.label2 === "Restricted"
      );
    },

    lastCheckedLabel() {
      const r = this.readiness;
      if (!r) return "";
      const iso =
        r.last_synced_at || (r.persisted && r.persisted.last_synced_at) || null;
      if (!iso) return "Never";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return this.relativeAge(d);
    },

    staleAgeSuffix() {
      const label = this.lastCheckedLabel;
      return label && label !== "Never" ? ` (checked ${label})` : "";
    },

    metaLine() {
      const parts = [];
      if (this.connect?.account_type) {
        parts.push(this.connect.account_type);
      }
      if (this.connect?.transfers_capability) {
        parts.push(
          `"Issue transfers" ${this.connect.transfers_capability}`
        );
      }
      if (this.connect?.last_payout?.arrival_date) {
        parts.push(`Last payout ${this.formatDate(this.connect.last_payout.arrival_date)}`);
      } else if (this.isConnected) {
        parts.push("No payouts yet");
      }
      return parts.join(" · ");
    },
  },

  watch: {
    // The page resolves the callback asynchronously after mount, so the prop
    // arrives AFTER the initial data() snapshot. Without this the swap panel
    // would never appear on a real return.
    oauthResult(next) {
      this.localOauthResult = next;
    },

    // Closing the dialog returns it to the default path. Without this, a
    // distributor who opened Advanced once would find the id field waiting
    // for them on every later visit -- the exact leading-with-the-field
    // shape this dialog was changed to stop. The typed value is cleared
    // with it so a stale id from an abandoned attempt cannot ride along on
    // the next authorize call and produce a spurious mismatch warning.
    existingDialog(open) {
      if (!open) {
        this.advancedOpen = false;
        this.intendedAccountId = "";
        this.existingError = "";
      }
    },
  },

  methods: {
    // Start the route-fork Connect onboarding flow: ask the backend to create
    // (or reuse) a Connect account + an onboarding AccountLink, then redirect
    // the browser to Stripe. On return, PaymentsPage re-syncs the live status.
    async onConnect() {
      if (this.connecting) return;
      this.connecting = true;
      try {
        const res = await startStripeConnect();
        const url = res && res.data && res.data.url;
        if (!url) {
          throw new Error("No onboarding URL returned");
        }
        window.location.assign(url);
      } catch (e) {
        this.connecting = false;
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch(
            "raiseError",
            "We couldn't start Stripe onboarding. Please try again."
          );
        }
      }
      // On success the browser navigates away; intentionally leave `connecting`
      // true so the button stays disabled through the redirect.
    },

    // Start the EXISTING-account path. This calls the OAuth authorize
    // endpoint and nothing else -- in particular it must never reach
    // startStripeConnect, which creates a new account. On failure it
    // surfaces the error and stops; "the OAuth start failed so let us just
    // make them an account" is the exact silent fallback this unit exists
    // to prevent.
    async onConnectExisting() {
      if (this.startingOauth) return;
      this.startingOauth = true;
      this.existingError = "";
      try {
        const intended = (this.intendedAccountId || "").trim() || null;
        const res = await startStripeConnectOAuth(intended);
        const url = res && res.data && res.data.url;
        if (!url) {
          throw new Error("No authorize URL returned");
        }
        window.location.assign(url);
      } catch (e) {
        this.startingOauth = false;
        this.existingError =
          "We couldn't start the Stripe connection. Please try again, or " +
          "contact ThreadFlow support if it keeps happening.";
      }
      // On success the browser navigates away; leave startingOauth true so
      // the button stays disabled through the redirect.
    },

    // Persist an account Stripe returned that was not the intended one.
    // Sends the SIGNED pending token minted by the callback -- never a bare
    // acct_, which the server would have no reason to trust.
    async onAcceptDifferentAccount() {
      if (this.accepting) return;
      this.accepting = true;
      try {
        await acceptDifferentStripeAccount(this.localOauthResult.pending_token);
        this.localOauthResult = null;
        this.$emit("connected");
      } catch (e) {
        this.accepting = false;
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch(
            "raiseError",
            "We couldn't save that Stripe account. Please try again."
          );
        }
      }
    },

    onCancelDifferentAccount() {
      this.localOauthResult = null;
      this.chooseAgainError = "";
    },

    // THE ESCAPE ROUTE, and the reason this panel is a stop-and-review
    // rather than a warning.
    //
    // Dismissing the panel would leave the distributor exactly where they
    // started -- an unpersisted sibling account, no connection, and no path
    // to the account they actually meant. So "Choose again" RE-ENTERS the
    // OAuth flow: back to Stripe, where they can sign in as the right
    // account and authorize it.
    //
    // It carries the intended acct_ back into the retry. They already told
    // us which account they meant; making them retype it is how a retry
    // turns into an abandonment, and the comparison safeguard only works if
    // the intent survives the round trip. On the unverified branch there is
    // no intent to carry, so this sends null -- the same value the blank
    // dialog sends, not an empty string, which the backend would have to
    // normalize a second way.
    //
    // It calls the OAUTH service and never startStripeConnect. Minting a
    // fresh empty account here would be the silent fallback this whole
    // surface exists to prevent, and it would look like success.
    async onChooseAgain() {
      if (this.choosingAgain) return;
      this.choosingAgain = true;
      this.chooseAgainError = "";

      const intended =
        (this.localOauthResult && this.localOauthResult.intended_account_id) ||
        null;

      try {
        const res = await startStripeConnectOAuth(intended);
        const url = res && res.data && res.data.url;
        if (!url) {
          throw new Error("No authorize URL returned");
        }
        window.location.assign(url);
      } catch (e) {
        this.choosingAgain = false;
        // The panel deliberately STAYS UP. Clearing it on a failed retry
        // would strand them with no explanation of the account Stripe just
        // made and no route back.
        this.chooseAgainError =
          "We couldn't send you back to Stripe. Please try again, or " +
          "contact ThreadFlow support if it keeps happening.";
      }
      // On success the browser navigates away; leave choosingAgain true so
      // the button stays disabled through the redirect.
    },

    // Cancel is inert: close the dialog, clear any error from a previous
    // failed attempt, touch nothing else. The card holds no optimistic
    // disconnect state to unwind precisely so that this stays true.
    onCancelDisconnect() {
      this.disconnectDialog = false;
      this.disconnectError = "";
    },

    // Deauthorize the tenant's Stripe account, then let the PARENT re-fetch.
    //
    // The refresh is an emit rather than a local mutation on purpose. The
    // card renders `connect`, a prop owned by PaymentsPage, so locally
    // hiding the button would leave the masked account id and a green
    // "Connected" badge sitting over an account that is gone -- the same
    // class of lie as the backend's old success-shaped 200. Emitting
    // "connected" (which PaymentsPage handles with @connected="load")
    // re-reads the real status, so badge, id and metadata all move together.
    async onConfirmDisconnect() {
      // Guards a double-click: without it, two deauthorize calls race and the
      // second lands on an account the first already cleared -- a spurious
      // 404 reported over a disconnect that actually succeeded.
      if (this.disconnecting) return;
      this.disconnecting = true;
      this.disconnectError = "";

      try {
        // No client id. The platform's identity is the server's to state --
        // this component used to hold its own literal copy, which had gone
        // stale, so every disconnect was rejected by Stripe while the UI said
        // nothing. See the contract note in billingService.
        await disconnectStripeConnect();
      } catch (e) {
        this.disconnecting = false;

        // 404 means there was nothing to disconnect. That is the state the
        // user was trying to reach, so reconcile the card rather than
        // shouting at them: the card is showing a connection the server says
        // does not exist, and the re-fetch is what corrects it. Treating
        // this as an error would report a failure for a successful outcome.
        if (e && e.response && e.response.status === 404) {
          this.disconnectDialog = false;
          this.$emit("connected");
          return;
        }

        // Anything else genuinely failed. The account is still connected, so
        // the dialog stays open carrying a real message and the card keeps
        // showing the account -- never a success claim over a failed
        // disconnect.
        this.disconnectError =
          "We couldn't disconnect this Stripe account. Please try again, or " +
          "contact ThreadFlow support if it keeps happening.";
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch("raiseError", this.disconnectError);
        }
        return;
      }

      this.disconnecting = false;
      this.disconnectDialog = false;
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch(
          "raiseSuccess",
          "Stripe account disconnected."
        );
      }
      this.$emit("connected");
    },

    // Map the fixed six-word vocabulary onto distributor-facing text.
    // The contract's raw Stripe values (card_payments: "inactive") stay in
    // the payload for admin/support and never reach this function's output.
    statusText(status) {
      switch (status) {
        case "ready":
          return "Ready";
        case "action_required":
          return "Action required";
        case "under_review":
          return "Under review";
        case "restricted":
          return "Restricted";
        case "not_used":
          return "Not used";
        default:
          return "Unknown";
      }
    },

    // Green is reserved for a CONFIRMED ready. When the data is cached or
    // stale we deliberately drop it to a neutral tone even if the cached
    // value says ready -- showing green over unconfirmed data is the exact
    // failure this whole unit exists to remove.
    statusTone(status) {
      if (this.isStaleOrErrored) return "muted";
      switch (status) {
        case "ready":
          return "ok";
        case "action_required":
        case "restricted":
          return "warn";
        case "under_review":
          return "info";
        default:
          return "muted";
      }
    },

    relativeAge(date) {
      const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
      if (seconds < 60) return "just now";
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
      const days = Math.floor(hours / 24);
      return `${days} day${days === 1 ? "" : "s"} ago`;
    },

    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      // arrival_date is a UTC date (PR-3 derives it from a Stripe unix ts at
      // UTC). Format in UTC so the day/month don't roll back in a US tz.
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    },
  },
};
</script>

<style scoped src="./integration-card.css"></style>

<style scoped>
/*
  The optional-id disclosure. Separated from the explanatory copy above it
  and set in a quieter register than the dialog's primary action, so the
  default path (Continue to Stripe, nothing entered) stays the obvious one.
*/
.tf-connect-advanced {
  margin-top: 12px;
}
.tf-connect-advanced-toggle {
  padding-left: 0;
  text-transform: none;
  letter-spacing: normal;
}
.tf-connect-advanced-body {
  margin-top: 8px;
}
.tf-connect-dialog-copy--hint {
  font-size: 13px;
  color: var(--tf-fg-4, #8a97a3);
}
.tf-readiness {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--tf-border-1, #e3e8ee);
}
.tf-readiness-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 4px 0;
  font-size: 13px;
}
.tf-readiness-row--meta {
  margin-top: 4px;
  font-size: 12px;
  color: var(--tf-fg-4, #8a97a3);
}
.tf-readiness-label {
  color: var(--tf-fg-3, #5a6773);
}
.tf-readiness-value {
  font-weight: 500;
}
.tf-readiness-value--ok {
  color: var(--tf-success, #1e8e3e);
}
.tf-readiness-value--warn {
  color: var(--tf-warning, #b26a00);
}
.tf-readiness-value--info {
  color: var(--tf-info, #1a73e8);
}
.tf-readiness-value--muted {
  color: var(--tf-fg-4, #8a97a3);
}
.tf-readiness-actions {
  margin-top: 10px;
}

/*
  THE STOP-AND-REVIEW PANEL.

  Its job is to be read, not clicked past, so the styling works against the
  usual instinct to compress a warning into one dismissible line: a real
  heading, paragraphs with breathing room, and actions that sit below the
  explanation rather than beside it. The distributor has to reach the
  consequence before they reach a button.
*/
.tf-connect-stop-heading {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 8px;
  color: var(--tf-fg-1, #1a2733);
}
.tf-connect-swap-copy {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 8px;
}
.tf-connect-swap-ids {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 8px;
}
.tf-connect-swap-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.tf-connect-swap-error {
  font-size: 13px;
  line-height: 1.5;
  margin-top: 10px;
  color: var(--tf-danger, #b3261e);
}
</style>
